import { useEffect, useRef, useState } from 'react';
import type { BoardState } from '@draughts/shared';
import { useAuthStore } from '../store/auth-store';
import { useStudyStore } from '../store/study-store';
import { runAfterProgrammaticPops } from './useOverlayBackButton';

/**
 * «Studia in editor», dal lato di chi lo offre: la revisione partita.
 *
 * Restituisce la funzione da chiamare col ply a schermo, oppure `null` quando
 * l'azione non c'e' — e allora il bottone non si disegna affatto, invece di
 * mostrarsi per poi non fare nulla.
 *
 * Due condizioni, e nessuna delle due riguarda DI CHI e' la partita che si sta
 * rivedendo (era il difetto: il bottone dipendeva da chi montava la review, e
 * quindi mancava nei tornei, nelle arene e nel profilo aperto in overlay):
 *
 *  1. un conto registrato — l'editor ha le stesse due uscite di sempre (gioca
 *     col computer, sfida un amico) e tutte e due ne hanno bisogno;
 *  2. nessuna partita viva nella schermata sotto. La review si apre anche sopra
 *     una partita in corso (il profilo dell'avversario toccato dalla scacchiera),
 *     e andarsene lascerebbe la partita: per quelle online App riporterebbe
 *     comunque sulla damiera nell'istante dopo, per quella locale nessuno
 *     riporterebbe niente e la partita resterebbe abbandonata in silenzio.
 *
 * ⚠️ Solo selettori reattivi, mai `.getState()`: i test dei componenti che
 * ospitano la review mockano gli store *per selettore* e non espongono
 * `getState`. Ed e' anche l'unico modo per cui il bottone SPARISCE quando una
 * partita comincia mentre la review e' aperta.
 */
export function useStudyInEditor(): ((position: BoardState) => void) | null {
  const registrato = useAuthStore((s) => s.isAuthenticated && !s.isGuest);
  const partitaInCorso = useStudyStore((s) => s.gameInProgress);
  const requestStudy = useStudyStore((s) => s.requestStudy);
  return registrato && !partitaInCorso ? requestStudy : null;
}

/**
 * …e lo stesso passaggio di mano dal lato di chi lo esegue: App.
 *
 * Pubblica nello store il solo fatto che la review non puo' leggere da se'
 * (`gameInProgress`) e raccoglie le richieste depositate, portandole all'editor.
 *
 * @param gameInProgress  `laPartitaDellaSchermataEInCorso(screen, …)`.
 * @param chiudiGliOverlayGlobali  i tre overlay montati fuori dal ramo della
 *   schermata (lobby, foglio «Gioca», sfoglia). Vanno chiusi QUI, e non lasciati
 *   all'effetto su `[screen]`: vedi sotto.
 * @param apriEditor  carica la posizione e passa alla schermata dell'editor.
 */
export function useStudyHandoff(
  gameInProgress: boolean,
  chiudiGliOverlayGlobali: () => void,
  apriEditor: (position: BoardState) => void,
): void {
  const pending = useStudyStore((s) => s.pending);
  const posizione = useRef<BoardState | null>(null);
  const [chiusuraChiesta, setChiusuraChiesta] = useState(false);

  useEffect(() => {
    useStudyStore.getState().setGameInProgress(gameInProgress);
  }, [gameInProgress]);

  /*
   * Primo tempo: si raccoglie la richiesta e si CHIUDE, senza navigare.
   *
   * Chiudere un overlay manda indietro la cronologia (`consumeOverlay` ->
   * `history.back()`), e quel back e' ASINCRONO: cambiare schermata mentre e'
   * ancora in volo spinge la voce nuova sotto il naso del riavvolgimento, che
   * se la mangia — e il primo «indietro» esce dall'app. E' lo stesso difetto,
   * e lo stesso rimedio, di `dalFoglioGioca` in App.
   *
   * Il perche' serva proprio qui e' verificato: alla review si arriva anche da
   * `PlayOverlay`/`CustomTableHost`, che stanno in `challengeOverlays` e quindi
   * sono montati in OGNI ramo, editor compreso. Quelli non si smontano col
   * cambio schermata: li chiuderebbe un commit dopo l'effetto su `[screen]`,
   * cioe' quando il `pushState` della schermata nuova e' gia' passato.
   */
  useEffect(() => {
    if (!pending) return;
    const chiesta = useStudyStore.getState().takeStudyRequest();
    if (!chiesta) return;
    /*
     * Una partita e' cominciata fra il render e il tocco (l'accoppiamento di un
     * torneo o di un'arena arriva quando vuole): la richiesta si butta invece di
     * restare in attesa e aprire l'editor piu' tardi, a sorpresa.
     */
    if (gameInProgress) return;
    posizione.current = chiesta;
    chiudiGliOverlayGlobali();
    setChiusuraChiesta(true);
  }, [pending, gameInProgress, chiudiGliOverlayGlobali]);

  /*
   * Secondo tempo, un commit dopo: adesso i riavvolgimenti degli overlay che si
   * stanno chiudendo sono stati CHIESTI, e `runAfterProgrammaticPops` puo'
   * aspettare che arrivino davvero. Armarlo nell'effetto qui sopra lo eseguirebbe
   * subito — contatore ancora a zero, nessuno ha chiesto niente — cioe'
   * esattamente nel commit sbagliato.
   */
  useEffect(() => {
    if (!chiusuraChiesta) return;
    const daAprire = posizione.current;
    posizione.current = null;
    setChiusuraChiesta(false);
    if (!daAprire) return;
    runAfterProgrammaticPops(() => apriEditor(daAprire));
  }, [chiusuraChiesta, apriEditor]);
}
